import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl } from "../lib/api";
import { enablePush, pushSupported, pushPermission } from "../lib/push";

const LAST_SEEN_KEY = "qdn_notif_last_seen";
const EMOJI = {
  promotional: "🎉",
  offer: "🏷️",
  order: "📦",
  delivery: "🛵",
  info: "📣",
};

function dateLabel(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [perm, setPerm] = useState(pushSupported() ? pushPermission() : "unsupported");

  useEffect(() => {
    api
      .get("/notifications")
      .then((r) => setItems(r.data.notifications || []))
      .catch(() => setItems([]));
    try {
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    (items || []).forEach((n) => {
      const label = dateLabel(n.at);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(n);
    });
    return Array.from(map.entries());
  }, [items]);

  const openTarget = (url) => {
    const target = url || "/";
    if (/^https?:\/\//i.test(target)) window.open(target, "_blank", "noopener");
    else navigate(target);
  };

  const enable = async () => {
    setEnabling(true);
    try {
      await enablePush();
      setPerm("granted");
      toast.success("Notifications enabled!");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FCF7F0]" data-testid="notifications-page">
      <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-orange-100 bg-white/95 px-4 py-3 backdrop-blur-md">
        <button
          type="button"
          data-testid="notifications-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="grid h-9 w-9 place-items-center rounded-full text-slate-700 transition-colors hover:bg-slate-100"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-lg font-black tracking-tight text-slate-900">Notifications</h1>
      </header>

      <div className="mx-auto max-w-xl px-4 py-4">
        {pushSupported() && perm !== "granted" && (
          <button
            type="button"
            data-testid="notifications-enable-banner"
            onClick={enable}
            disabled={enabling}
            className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-white p-3 text-left shadow-sm disabled:opacity-60"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#FF5200] text-lg text-white">
              🔔
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-extrabold text-slate-900">Turn on notifications</span>
              <span className="block text-xs text-slate-600">Get order updates, offers &amp; delivery alerts.</span>
            </span>
            <span className="shrink-0 rounded-full bg-[#FF5200] px-3 py-1.5 text-xs font-bold text-white">
              {enabling ? "…" : "Allow"}
            </span>
          </button>
        )}

        {items === null && <p className="py-16 text-center text-sm text-slate-400">Loading…</p>}

        {items && items.length === 0 && (
          <div data-testid="notifications-empty" className="flex flex-col items-center justify-center py-20 text-center">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-slate-100 text-slate-400">
              <BellOff size={28} />
            </div>
            <p className="mt-4 text-base font-extrabold text-slate-900">No new notifications</p>
            <p className="mt-1 text-sm text-slate-500">We&apos;ll let you know when something arrives.</p>
          </div>
        )}

        {groups.map(([label, list]) => (
          <div key={label} className="mb-5">
            <p className="mb-2 px-1 text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
            <div className="space-y-2.5">
              {list.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  data-testid={`notification-card-${n.id}`}
                  onClick={() => openTarget(n.url)}
                  className="flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-white p-3 text-left shadow-sm transition-transform active:scale-[0.99]"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-orange-50 text-xl">
                    {EMOJI[n.ntype] || "🔔"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-extrabold text-slate-900">{n.title}</span>
                      <span className="shrink-0 text-[10px] font-semibold text-slate-400">{timeLabel(n.at)}</span>
                    </span>
                    {n.body && <span className="mt-0.5 block text-xs leading-5 text-slate-600">{n.body}</span>}
                    {n.cta && (
                      <span className="mt-1.5 inline-block rounded-full bg-[#FF5200] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                        {n.cta}
                      </span>
                    )}
                  </span>
                  {n.image && (
                    <img
                      src={assetUrl(n.image)}
                      alt=""
                      className="h-14 w-14 shrink-0 rounded-xl object-cover"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
